import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { FileText, Download } from 'lucide-react';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return '—'; } };

const th = { padding: '0.7rem 1rem' };
const td = { padding: '0.7rem 1rem' };
const kpiN = { fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 800, color: '#002A38' };
const kpiL = { fontSize: '0.72rem', color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.04em' };

export function AdminInvoicesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getSelfAssessmentInvoices()
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    const rows = data?.invoices || [];
    const head = ['Invoice', 'Name', 'Email', 'Base (INR)', 'GST (INR)', 'Total (INR)', 'Date', 'Status', 'Payment ID'];
    const lines = rows.map((r) => [r.invoice_number, r.candidate_name, r.candidate_email, r.base_inr, r.gst_inr, r.total_inr, r.paid_at ? new Date(r.paid_at).toISOString().slice(0, 10) : '', r.status, r.payment_id]
      .map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `chire-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: '2rem', color: '#78716C' }}>Loading invoices…</div>;
  const invoices = data?.invoices || [];

  return (
    <div data-testid="admin-invoices-tab">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div><div style={kpiN} data-testid="admin-invoices-count">{data?.count || 0}</div><div style={kpiL}>Invoices</div></div>
          <div><div style={kpiN} data-testid="admin-invoices-total">{inr(data?.total_inr)}</div><div style={kpiL}>Total collected (incl. GST)</div></div>
        </div>
        {invoices.length > 0 && (
          <button data-testid="admin-invoices-export" onClick={exportCsv}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#002A38', color: '#fff', border: 'none', borderRadius: 999, padding: '0.55rem 1.1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
            <Download size={15} /> Export CSV
          </button>
        )}
      </div>

      {invoices.length === 0 ? (
        <div data-testid="admin-invoices-empty" style={{ background: '#fff', border: '1px dashed #ECE7DE', borderRadius: 14, padding: '3rem', textAlign: 'center', color: '#78716C' }}>
          <FileText size={36} color="#D6CFC6" style={{ margin: '0 auto 0.75rem' }} />
          No self-serve assessment invoices yet.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 760 }}>
              <thead>
                <tr style={{ background: '#EDF4F7', textAlign: 'left', color: '#78716C', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={th}>Invoice #</th><th style={th}>Candidate</th><th style={th}>Date</th>
                  <th style={{ ...th, textAlign: 'right' }}>Base</th><th style={{ ...th, textAlign: 'right' }}>GST</th>
                  <th style={{ ...th, textAlign: 'right' }}>Total</th><th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((r) => (
                  <tr key={r.invoice_number} data-testid={`admin-invoice-row-${r.invoice_number}`} style={{ borderTop: '1px solid #F1EDE8' }}>
                    <td style={{ ...td, fontWeight: 700, color: '#002A38' }}>{r.invoice_number}</td>
                    <td style={td}><div style={{ fontWeight: 600, color: '#002A38' }}>{r.candidate_name}</div><div style={{ color: '#A8A29E', fontSize: '0.75rem' }}>{r.candidate_email}</div></td>
                    <td style={{ ...td, color: '#57534E' }}>{fmt(r.paid_at)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#57534E' }}>{inr(r.base_inr)}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#57534E' }}>{inr(r.gst_inr)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#002A38' }}>{inr(r.total_inr)}</td>
                    <td style={td}><span style={{ background: r.completed ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.14)', color: r.completed ? '#059669' : '#B45309', borderRadius: 999, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700 }}>{r.completed ? 'Completed' : 'Paid'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
